import type { Observable } from 'rxjs';
import { filter, Subject, scan, startWith } from 'rxjs';

/**
 * Process-local bus of "this work order task changed in ServiceNow" signals. The inbound SN
 * webhook (and the FE's own actions) publish a WOT number here; the per-ticket subscription
 * filters to its own number and pushes a tick to every client currently viewing that ticket,
 * which triggers a live re-fetch.
 *
 * The set of active subscribers IS the "open tickets" registry: a signal for a ticket nobody
 * is watching is simply dropped — the next read/reload is live from ServiceNow and already
 * correct, so there is nothing to do. That's why we don't keep an explicit Map (unlike
 * _pending-ops.ts, whose entries must outlive any single request).
 *
 * SINGLE-INSTANCE ONLY — same constraint as _pending-ops.ts. With >1 replica a webhook may
 * land on a different process than the one holding the viewer's subscription; the viewer then
 * falls back to the existing manual refreshes (post-action refetch, reload).
 */
const ticketChanged$ = new Subject<string>();

/** Publish a change for a single work order task (by WOT number). */
export function publishTicketChanged(ticketNumber: string): void {
  ticketChanged$.next(ticketNumber);
}

/**
 * A stream of ticks for one ticket: emits 0 immediately (so the subscription has an initial
 * value) and increments on every change published for that ticket. Consumers only care that
 * the value changed, not what it is.
 */
export function ticketChangeTicks(ticketNumber: string): Observable<number> {
  return ticketChanged$.pipe(
    filter(n => n === ticketNumber),
    scan(count => count + 1, 0),
    startWith(0),
  );
}
